import { useQuery } from "@tanstack/react-query";
import { conversationsApi } from "@/services/conversation-service";
import { useConversationScope } from "@/hooks/useConversationScope";
import type { Conversations } from "@/types";
import { conversationQueryKeys } from "@/features/playground/conversation-query-keys";

/** The design shows a short recent list, not the full history. */
export const SIDEBAR_CONVERSATION_LIMIT = 5;

/**
 * The recent conversations shown in the sidebar.
 *
 * The key carries a "sidebar" segment on purpose. Playground's own scoped list
 * query asks for 50 rows grouped by date;
 * this asks for 5 flat ones. Sharing a key between two different requests would
 * mean whichever resolved first filled the cache for both, and the loser would
 * render the wrong shape.
 *
 * The cost is one small extra request. Making them share would mean one query
 * serving both, which is worth doing when Playground's list moves onto a common
 * hook — see useConversationScope.
 */
export const useSidebarConversations = (enabled = true) => {
  const { scope, requestScope, ready } = useConversationScope();

  const query = useQuery<Conversations>({
    queryKey: conversationQueryKeys.sidebar(scope),
    queryFn: () =>
      conversationsApi.getConversations({
        skip: 0,
        limit: SIDEBAR_CONVERSATION_LIMIT,
        include_archived: false,
        // conversation-service.js is plain JS; its inferred parameter type has
        // no optional properties, so every field has to be passed.
        search: "",
        requestScope,
      }) as Promise<Conversations>,
    enabled: enabled && ready,
    staleTime: 60_000,
  });

  return {
    ...query,
    // A disabled pending query is not `isLoading` in Query v5. Keep the
    // established loading presentation until identity bootstrap has completed
    // instead of rendering a successful-looking empty scope.
    isLoading: enabled && (!ready || query.isLoading),
  };
};
